import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import PageShell, { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { FormGrid, Field, Select, Input } from '../../ui/Form';
import { sectionOptions } from '../../ui/labels';
import HelpButton from '../help/HelpButton';

/** Enrolling a student who has no current section (new registration or
 *  after re-admission) — moving an already-enrolled student is a
 *  transfer, handled from the Students page instead. */
export default function Enrollments() {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(async () => {
    const [enrollments, students, sections] = await Promise.all([
      api.enrollments(), api.students(), api.sections(),
    ]);
    return { enrollments, students, sections };
  }, []);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ student_id: '', section_id: '', start_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const dirty = useDirtyForm(form);

  const students = data?.students || [];
  const sections = data?.sections || [];
  const enrollments = data?.enrollments || [];

  const enrolledStudentIds = new Set(enrollments.filter((e) => !e.end_date).map((e) => e.student_id));
  const unenrolled = students.filter((s) => s.status === 'active' && !enrolledStudentIds.has(s.id));

  // Names come on the rows from the server.
  const rows = enrollments.map((e) => ({
    ...e,
    student_name: e.student_name || `#${e.student_id}`,
    section_title: e.section_label || `#${e.section_id}`,
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.student_id || !form.section_id) return toast.error('الطالب والشعبة مطلوبان');
    setSaving(true);
    try {
      await api.createEnrollment({
        student_id: Number(form.student_id),
        section_id: Number(form.section_id),
        start_date: form.start_date,
      });
      toast.success('تم القيد');
      setCreating(false);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="قيود الطلاب" description="قيد طالب جديد في شعبة"
      actions={(
        <>
          <HelpButton guides={['student-enroll']} />
          <Button
            data-guide="enrollments-add"
            onClick={() => {
              const next = { student_id: '', section_id: '', start_date: new Date().toISOString().slice(0, 10) };
              setForm(next); dirty.reset(next); setCreating(true);
            }}
          >
            + قيد جديد
          </Button>
        </>
      )}>
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        keyField="id"
        rows={rows}
        loading={loading}
        emptyText="لا توجد قيود"
        searchPlaceholder="ابحث باسم الطالب..."
        columns={[
          { key: 'student_name', label: 'الطالب', width: 2, filterable: false, render: (r) => <strong>{r.student_name}</strong> },
          { key: 'section_title', label: 'الشعبة', nowrap: true, align: 'center',
            render: (r) => <span className="ui-badge ui-badge-info">{r.section_title}</span> },
          { key: 'start_date', label: 'تاريخ البدء', nowrap: true, filterable: false },
          { key: 'end_date', label: 'الحالة', nowrap: true, align: 'center', filterable: false,
            render: (r) => r.end_date
              ? <span className="ui-badge ui-badge-neutral">منتهي {r.end_date}</span>
              : <span className="ui-badge ui-badge-success">فعّال</span> },
        ]}
      />

      {creating && (
        <Modal open size="md" title="قيد طالب جديد" onClose={() => setCreating(false)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving} data-guide="modal-save">قيد</Button>
            </>
          )}>
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <Field label="الطالب" required span guide="enroll-student"
                hint={unenrolled.length === 0 ? 'كل الطلاب النشطين لديهم قيد حالي — استخدم النقل بدلاً من ذلك' : undefined}>
                <Select value={form.student_id} onChange={set('student_id')} placeholder="اختر الطالب"
                  disabled={unenrolled.length === 0}
                  options={unenrolled.map((s) => ({ value: s.id, label: s.name }))} />
              </Field>
              <Field label="الشعبة" required guide="enroll-section">
                <Select value={form.section_id} onChange={set('section_id')} placeholder="اختر الشعبة"
                  options={sectionOptions(sections.filter((s) => s.status === 'active'), { withCount: true })} />
              </Field>
              <Field label="تاريخ البدء" required guide="enroll-date"><Input type="date" value={form.start_date} onChange={set('start_date')} /></Field>
            </FormGrid>
          </form>
        </Modal>
      )}
    </PageShell>
  );
}
